import { useCallback, useMemo, useState, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Container, DataGrid, KeenIcon } from '@/components';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import usePagination from '@/hooks/usePagination';
import {
  useGetServiceProvidersQuery,
  useChangeServiceProviderStatusMutation,
  useApproveServiceProviderMutation,
  useRejectServiceProviderMutation,
} from '@/services/ServiceProviderService';
import {
  ServiceProviderResponse,
  ServiceProviderStatus,
} from '@/types/service-provider.types';
import { ErrorApiResponse, PaginatedApiResponse } from '@/types/api.types';
import { usePermissions } from '@/hooks/usePermissions';
import { setSelectedServiceProviderId } from '@/store/authSlice';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { setServiceProviderCapabilities } from '@/store/serviceProviderCapabilitiesSlice';
import { formatInternationalPhoneNumber } from '@/utils';

const ServiceProviderTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [statusFilter, setStatusFilter] = useState<
    ServiceProviderStatus | 'all'
  >('all');

  const {
    pagination,
    setPagination,
    handleSearch,
    searchTerm,
    resetSearch,
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
  } = usePagination();

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      exclude_pending: true, // Exclude pending providers from main list
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedValue, statusFilter]
  );

  const {
    data: serviceProvidersData,
    isLoading,
    isFetching,
    isError,
    refetch: refetchServiceProviders,
  } = useGetServiceProvidersQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  }) as {
    data: PaginatedApiResponse<ServiceProviderResponse[]> | undefined;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  const [changeStatus] = useChangeServiceProviderStatusMutation();

  const [approveServiceProvider] = useApproveServiceProviderMutation();

  const [rejectServiceProvider] = useRejectServiceProviderMutation();
  const { hasPermission } = usePermissions();
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    showReasonInput?: boolean;
    onConfirm: (reason?: string) => void;
  }>({
    open: false,
    title: '',
    message: '',
    showReasonInput: false,
    onConfirm: () => {},
  });

  const openConfirm = useCallback(
    (
      title: string,
      message: string,
      onConfirm: (reason?: string) => void,
      showReasonInput = false
    ) => {
      setConfirmModal({
        open: true,
        title,
        message,
        onConfirm,
        showReasonInput,
      });
    },
    []
  );

  const filteredData = useMemo(() => {
    if (!serviceProvidersData?.data) return [];
    return serviceProvidersData.data;
  }, [serviceProvidersData?.data]);

  useEffect(() => {
    if (serviceProvidersData) {
      handleTotalPages(serviceProvidersData.total_pages || 1);
      handleTotalItems(serviceProvidersData.total || 0);
    }
  }, [serviceProvidersData, handleTotalPages, handleTotalItems]);
  const handleStatusChange = useCallback(
    async (id: string, newStatus: ServiceProviderStatus, reason?: string) => {
      try {
        if (newStatus === ServiceProviderStatus.ACTIVE) {
          await approveServiceProvider({
            id,
            data: { status: newStatus },
          }).unwrap();
        } else if (newStatus === ServiceProviderStatus.REJECTED) {
          await rejectServiceProvider({
            id,
            data: {
              status: newStatus,
              ...(reason && { reason: reason }),
            },
          }).unwrap();
        } else {
          await changeStatus({
            id,
            data: { status: newStatus },
          }).unwrap();
        }

        enqueueSnackbar(
          `Service Provider ${
            newStatus === ServiceProviderStatus.ACTIVE
              ? 'approved'
              : newStatus === ServiceProviderStatus.REJECTED
                ? 'rejected'
                : 'status updated'
          } successfully`,
          { variant: 'success' }
        );

        await refetchServiceProviders();
      } catch (error) {
        const apiError = error as ErrorApiResponse;
        enqueueSnackbar(apiError?.data?.message || 'Failed to update status', {
          variant: 'error',
        });
      }
    },
    [
      approveServiceProvider,
      rejectServiceProvider,
      changeStatus,
      enqueueSnackbar,
      refetchServiceProviders,
    ]
  );
  const handleArchiveStatusChange = useCallback(
    async (id: string, newStatus: ServiceProviderStatus) => {
      try {
        await changeStatus({
          id,
          data: { status: newStatus },
        }).unwrap();

        enqueueSnackbar(
          `Service Provider ${
            newStatus === ServiceProviderStatus.ACTIVE
              ? 'approved'
              : newStatus === ServiceProviderStatus.REJECTED
                ? 'rejected'
                : 'status updated'
          } successfully`,
          { variant: 'success' }
        );

        await refetchServiceProviders();
      } catch (error) {
        const apiError = error as ErrorApiResponse;
        enqueueSnackbar(apiError?.data?.message || 'Failed to update status', {
          variant: 'error',
        });
      }
    },
    [
      approveServiceProvider,
      rejectServiceProvider,
      changeStatus,
      enqueueSnackbar,
      refetchServiceProviders,
    ]
  );
  const handleConfirmAction = useCallback(
    (id: string, newStatus: ServiceProviderStatus) => {
      const isApprove = newStatus === ServiceProviderStatus.ACTIVE;
      const isReject = newStatus === ServiceProviderStatus.REJECTED;
      const actionText = isApprove
        ? 'approve'
        : isReject
          ? 'reject'
          : 'update status';

      openConfirm(
        `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} Service Provider`,
        `Are you sure you want to ${actionText} this service provider?${isReject ? ' A reason is required.' : ''}`,
        async (reason?: string) => {
          try {
            await handleStatusChange(id, newStatus, reason);
            enqueueSnackbar(
              `Service Provider ${
                newStatus === ServiceProviderStatus.ACTIVE
                  ? 'approved'
                  : newStatus === ServiceProviderStatus.REJECTED
                    ? 'rejected'
                    : 'status updated'
              } successfully`,
              { variant: 'success' }
            );
            refetchServiceProviders();
          } catch (error) {
            console.error(`Error ${actionText}ing service provider:`, error);
            enqueueSnackbar(
              `Failed to ${actionText} service provider. Please try again.`,
              { variant: 'error' }
            );
          }
        },
        isReject // Show reason input only for rejections
      );
    },
    [openConfirm, handleStatusChange, enqueueSnackbar, refetchServiceProviders]
  );

  const handleServiceProviderDetails = useCallback(
    async (serviceProviderId: string) => {
      try {
        // Fetch the service provider's current plan
        const currentPlan =
          await SubscriptionsService.getCurrentServiceProviderPlan(
            serviceProviderId
          );
        const planId = currentPlan?.subscription?.id;

        if (planId) {
          // Fetch capabilities for the plan
          const capabilities =
            await SubscriptionsService.getSubscriptionCapabilities(planId, serviceProviderId);

          // Extract unique keys and store in Redux
          const uniqueKeys =
            capabilities
              ?.filter((cap) => cap.include === true)
              .map((cap) => cap.unique_key) || [];

          dispatch(
            setServiceProviderCapabilities({ capabilities: uniqueKeys })
          );
        } else {
          // No plan found, clear capabilities
          dispatch(setServiceProviderCapabilities({ capabilities: [] }));
        }

        // Navigate to service provider details
        navigate(`/service-providers/details`);
        dispatch(
          setSelectedServiceProviderId({
            selectedServiceProviderId: serviceProviderId,
          })
        );
      } catch (error) {
        console.error('Failed to fetch service provider capabilities:', error);
        // Navigate anyway, capabilities will be empty
        dispatch(setServiceProviderCapabilities({ capabilities: [] }));
        navigate(`/service-providers/details`);
        dispatch(
          setSelectedServiceProviderId({
            selectedServiceProviderId: serviceProviderId,
          })
        );
      }
    },
    [dispatch, navigate]
  );

  const getStatusBadge = (status: ServiceProviderStatus) => {
    switch (status) {
      case ServiceProviderStatus.PENDING:
        return <span className="badge badge-info">Pending</span>;
      case ServiceProviderStatus.ACTIVE:
        return <span className="badge badge-success">Active</span>;
      case ServiceProviderStatus.ARCHIVED:
        return <span className="badge badge-danger">Archived</span>;
      case ServiceProviderStatus.SUSPENDED:
        return <span className="badge badge-warning">Suspended</span>;
      case ServiceProviderStatus.REJECTED:
        return <span className="badge badge-danger">Rejected</span>;
      default:
        return (
          <span className="badge badge-secondary contrast:text-black">
            Unknown
          </span>
        );
    }
  };

  const columns = useMemo<ColumnDef<ServiceProviderResponse>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'Display Name',
        cell: ({ row }) => (
          <button
            type="button"
            onClick={async () => {
              await handleServiceProviderDetails(row.original.id);
            }}
            className="font-medium block text-gray-900 hover:text-primary-active contrast:hover:text-blue-200 text-left focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{
              minWidth: 0,
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.display_name}
          >
            {row.original.display_name}
          </button>
        ),
      },
      {
        accessorKey: 'contact_email',
        header: 'Contact Email',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '200px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.contact_email}
          >
            {row.original.contact_email}
          </span>
        ),
      },
      {
        accessorKey: 'contact_phone',
        header: 'Contact Phone',
        cell: ({ row }) => (
          <span
            className="break-words whitespace-nowrap"
            style={{ minWidth: 0 }}
          >
            {row.original.contact_phone
              ? formatInternationalPhoneNumber(
                  row.original.contact_phone,
                  row.original.country_code,
                  row.original.contact_phone_country_short_code
                )
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: ({ row }) => (
          <span
            className="block"
            style={{
              minWidth: 0,
              maxWidth: '120px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={row.original.country}
          >
            {row.original.country}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => getStatusBadge(row.original.status),
      },
      {
        accessorKey: 'archive_status',
        header: 'Archive Status',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.status === ServiceProviderStatus.PENDING ? (
              <span className="w-full text-sm font-medium text-center">-</span>
            ) : hasPermission('service_provider_management_update') ? (
              <select
                value={row.original.status}
                onChange={(e) =>
                  handleArchiveStatusChange(
                    row.original.id,
                    Number(e.target.value) as ServiceProviderStatus
                  )
                }
                className="select select-sm"
                aria-label="Change service provider status"
                style={{ minWidth: '120px' }}
              >
                <option value={ServiceProviderStatus.ACTIVE}>Active</option>
                <option value={ServiceProviderStatus.ARCHIVED}>Archived</option>
                <option value={ServiceProviderStatus.SUSPENDED}>
                  Suspended
                </option>
              </select>
            ) : (
              <span
                className={`badge ${
                  row.original.status === ServiceProviderStatus.ACTIVE
                    ? 'badge-success'
                    : row.original.status === ServiceProviderStatus.ARCHIVED
                      ? 'badge-danger'
                      : 'badge-warning'
                }`}
              >
                {row.original.status === ServiceProviderStatus.ACTIVE
                  ? 'Active'
                  : row.original.status === ServiceProviderStatus.ARCHIVED
                    ? 'Archived'
                    : 'Suspended'}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-2">
            {/* View button - always show for all statuses */}
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              onClick={async () => {
                await handleServiceProviderDetails(row.original.id);
              }}
              title="View Details"
              aria-label={`View ${row.original.display_name} details`}
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>

            {row.original.status === ServiceProviderStatus.PENDING &&
            hasPermission('service_provider_management_update') ? (
              <>
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() =>
                    handleConfirmAction(
                      row.original.id,
                      ServiceProviderStatus.ACTIVE
                    )
                  }
                  title="Approve"
                >
                  <KeenIcon icon="check" className="text-base" />
                </button>
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() =>
                    handleConfirmAction(
                      row.original.id,
                      ServiceProviderStatus.REJECTED
                    )
                  }
                  title="Reject"
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              </>
            ) : (
              <>
                {hasPermission('service_provider_management_update') && (
                  <button
                    className="btn btn-sm btn-icon btn-light"
                    onClick={() =>
                      navigate(`/service-providers/edit/${row.original.id}`, {
                        state: { from: '/service-management' },
                      })
                    }
                    title="Edit"
                  >
                    <KeenIcon icon="pencil" className="text-base" />
                  </button>
                )}
              </>
            )}
          </div>
        ),
      },
    ],
    [
      handleStatusChange,
      handleConfirmAction,
      hasPermission,
      navigate,
      handleServiceProviderDetails,
    ]
  );

  return (
    <>
      <Container>
        <div
          className="card card-grid"
          style={{ minWidth: 0, width: '100%', overflowX: 'auto' }}
        >
          <div
            className="card-header flex flex-wrap items-center gap-2 sm:gap-4 justify-start"
            style={{ minWidth: 0 }}
          >
            <div
              className="flex items-center gap-2"
              style={{
                minWidth: 0,
                flexWrap: 'nowrap',
              }}
            >
              <label
                htmlFor="search-service-providers"
                className="input input-sm h-8 sm:h-10 flex items-center"
                style={{ minWidth: '180px', width: '240px', flexShrink: 1 }}
                aria-label="Search service providers"
              >
                <KeenIcon
                  icon="magnifier"
                  className="text-gray-500 flex-shrink-0"
                  aria-hidden="true"
                />
                <input
                  id="search-service-providers"
                  placeholder="Search service providers"
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-full h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                  aria-label="Search service providers by name, email, or phone"
                  style={{ minWidth: 0 }}
                />
              </label>
              <select
                id="status-filter"
                className="select select-sm h-8 sm:h-10 text-sm"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as ServiceProviderStatus | 'all'
                  )
                }
                aria-label="Filter service providers by status"
                style={{ minWidth: '110px', width: '130px', flexShrink: 0 }}
              >
                <option value="all">All Status</option>
                <option value={ServiceProviderStatus.ACTIVE}>Active</option>
                <option value={ServiceProviderStatus.ARCHIVED}>Archived</option>
                <option value={ServiceProviderStatus.SUSPENDED}>
                  Suspended
                </option>
              </select>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <button
                type="button"
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 whitespace-nowrap"
                onClick={() => {
                  resetSearch();
                  setStatusFilter('all');
                }}
                aria-label="Clear all search and status filters"
                style={{ flexShrink: 0 }}
              >
                <KeenIcon
                  icon="arrows-circle"
                  className="text-gray-600"
                  aria-hidden="true"
                />
                <span>Clear Filters</span>
              </button>
            )}
          </div>
          <div className="card-body">
            {isLoading ? (
              <div className="text-center py-4">
                Loading service providers...
              </div>
            ) : isError ? (
              <div className="text-center py-4 text-red-500">
                Failed to load service providers. Please try again.
              </div>
            ) : (
              <DataGrid
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  more: true,
                  totalItems,
                  totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'display_name', desc: false }]}
                serverSide={true}
                onFilterReset={() => {
                  resetSearch();
                  setStatusFilter('all');
                }}
                hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
                key={`${JSON.stringify(queryParams)}-${filteredData.length}-${isFetching}`}
              />
            )}
          </div>
        </div>
      </Container>

      <ConfirmationModal
        open={confirmModal.open}
        onClose={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={(reason) => {
          confirmModal.onConfirm(reason);
          setConfirmModal((prev) => ({ ...prev, open: false }));
        }}
        showReasonInput={confirmModal.showReasonInput}
        confirmButtonVariant={
          confirmModal.showReasonInput ? 'danger' : 'primary'
        }
        confirmLabel={
          confirmModal.showReasonInput ? 'Confirm Rejection' : 'Yes'
        }
        cancelLabel="Cancel"
      />
    </>
  );
};

export default ServiceProviderTab;
